// Tier 4 events → apiv2 `clinical_event` over the session (never the WF50 webhook). apiv2 reads
// ONE `clinical_payload` JSON param, so wrap, don't flatten. The patient travels only as
// `id_dev_user` (the id apiv2 checks, and the one WF5 routes on); attribution rides in `actor`.

import { ws } from './wiservice';

// The 12 forgeable identity keys (mirrors WF50's GUARDED set) — never sent raw from the browser.
// WF5's Normalize Event strips them server-side too (S-117); attribution rides the `actor` wrapper.
const DROP = new Set([
  'withings_user_id', 'patient_id', 'userid',
  'sent_by', 'sent_by_name', 'sent_by_firstname', 'sent_by_lastname', 'sent_by_role',
  'author_user_id', 'author_firstname', 'author_lastname', 'author_role',
]);

// Nothing may depend on WF5's fallback to this key (spec 2026-10-06 §9), so a regressed caller fails here.
const RETIRED_PATIENT_KEY = 'target_withings_user_id';

const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Fire a Tier 4 event through apiv2 `clinical_event`. Returns WF5's result for
 *  sync events, the `{status:"received"}` ack for async. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- JS-interop envelope
export const eventPost = async (eventType: string, body: Record<string, unknown> = {}): Promise<any> => {
  if (RETIRED_PATIENT_KEY in body || (isPlainObject(body.payload) && RETIRED_PATIENT_KEY in body.payload)) {
    throw new Error(`eventPost('${eventType}'): ${RETIRED_PATIENT_KEY} is retired — send the patient as id_dev_user`);
  }
  const payload: Record<string, unknown> = Object.fromEntries(
    Object.entries(body).filter(([k, v]) => v !== undefined && v !== null && !DROP.has(k)),
  );
  // Strip guarded keys from a nested free-form `payload` blob too (mirrors WF50's two-scope scan).
  const blob = payload.payload;
  if (isPlainObject(blob)) {
    payload.payload = Object.fromEntries(Object.entries(blob).filter(([k]) => !DROP.has(k)));
  }
  const res = await ws('clinical_event', eventType, { clinical_payload: JSON.stringify(payload) });
  return res.body;
};
